import { useMemo, useContext, useCallback } from "react";
import FormWizard, {
  formVariantsEnum,
} from "../../../Forms/FormWizard/FormWizard";
import FindingsStep from "./FindingsStep/FindingsStep";
import TicketingSystemStep from "./TicketingSystemStep/TicketingSystemStep";
import CustomizationStep from "./CustomizationStep/CustomizationStep";
import TicketMonitoringStep from "./TicketMonitoringStep/TicketMonitoringStep";
import TicketSchedulingStep from "./TicketSchedulingStep/TicketSchedulingStep";
import { useHistory, useParams } from "react-router-dom";
import {
  AutomationTypeEnum,
  AutomationFormBaseFields,
  validateAutomationData,
} from "../../AutomationsUtils";
import useAutomationSubmit from "../useAutomationSubmit/useAutomationSubmit";
import { useQuery, MutationTuple, useApolloClient } from "@apollo/client";
import { GET_AUTOMATION } from "../../AutomationsAPI";
import { useMutationWrapper, PageNotFound } from "@SeemplicityDev/shared";
import { SYNC_QUEUE } from "../../../RemediationQueues/RemediationQueue/RemediationQueueForm/RemediationQueueFormApi";
import { showToast } from "../../../Forms/Dialogs/Toast/Toast";
import useAutomationURLData, {
  AutomationURLDataReturn,
} from "../useAutomationURLData/useAutomationURLData";
import { FieldsUtilsContext } from "./FieldsUtilsProvider";
import useAutomationWizardName from "../useAutomationWizardName/useAutomationWizardName";
import {
  integrationTypeToPageLinkMap,
  ticketEndpointTypeEnum,
} from "@SeemplicityDev/ticket-master";

export interface TicketingQueueWizardFormFields
  extends AutomationFormBaseFields {
  integrationType: string;
  queueSize: number;
  ticketProvider: string;
  ticketsReopenEnabled: boolean;
  ticketsReopenUnlimited: boolean;
  daysToReopenTickets?: number;
  ticketsReopenStartThreshold?: number;
  enableSlaReminder: boolean;
  daysBeforeSlaToRemind?: number;
  slaReminderDaysInterval?: number;
  slaReminderMaxTimes?: number;
}

interface TicketingQueueOutputParams {
  ticket_provider_id: string;
  queue_size: number;
  days_to_reopen_tickets: number | null;
  tickets_sla_reminder_enabled: boolean;
  tickets_sla_reminder_days_interval?: number;
  tickets_sla_reminder_max_times?: number;
  tickets_sla_reminder_days_before_to_start?: number;
  tickets_reopen_enabled?: boolean;
  tickets_reopen_start_threshold?: number;
  tickets_reopen_unlimited?: boolean;
  template_id: number;
  report_fields: string[];
  keys: Record<string, any>;
  fields: Record<string, any>;
}

interface TicketingQueueInputParams {
  ticket_provider_id: string;
  ticket_provider_type: string;
  field_mapping_id: string;
  queue_size: number;
  days_to_reopen_tickets: number;
  template_id: number;
  tickets_sla_reminder_enabled: boolean;
  tickets_sla_reminder_days_interval?: number;
  tickets_sla_reminder_max_times?: number;
  tickets_sla_reminder_days_before_to_start?: number;
  tickets_reopen_enabled?: boolean;
  tickets_reopen_start_threshold?: number;
  tickets_reopen_unlimited?: boolean;
}

interface syncQueueMutationVariablesType {
  variables: {
    remediation_queue_id: string;
  };
}

interface syncQueueMutationResponseType {
  data: {
    remediation_queue_sync_now: {
      tickets_opened: number;
      tickets_failed_to_open: number;
    };
  };
}

export const TICKETS_REOPEN_START_THRESHOLD_DEFAULT_VALUE = 90;

export const reopenFieldsFromParams = (params: {
  tickets_reopen_enabled?: boolean;
  tickets_reopen_start_threshold?: number;
  tickets_reopen_unlimited?: boolean;
  days_to_reopen_tickets?: number | null;
}): Pick<
  TicketingQueueWizardFormFields,
  | "ticketsReopenEnabled"
  | "ticketsReopenUnlimited"
  | "ticketsReopenStartThreshold"
> => ({
  ticketsReopenEnabled: Boolean(
    params.tickets_reopen_enabled ?? (params.days_to_reopen_tickets ?? 0) > 0
  ),
  ticketsReopenUnlimited: Boolean(params.tickets_reopen_unlimited),
  ticketsReopenStartThreshold:
    params.tickets_reopen_start_threshold ??
    TICKETS_REOPEN_START_THRESHOLD_DEFAULT_VALUE,
});

export const reopenParamsFromFields = (fields: {
  ticketsReopenEnabled: boolean;
  ticketsReopenUnlimited?: boolean;
  daysToReopenTickets?: number;
  ticketsReopenStartThreshold?: number;
}): Pick<
  TicketingQueueOutputParams,
  | "days_to_reopen_tickets"
  | "tickets_reopen_enabled"
  | "tickets_reopen_start_threshold"
  | "tickets_reopen_unlimited"
> => ({
  days_to_reopen_tickets: fields.ticketsReopenEnabled
    ? Number(fields.daysToReopenTickets)
    : null,
  tickets_reopen_enabled: fields.ticketsReopenEnabled,
  tickets_reopen_unlimited: Boolean(fields.ticketsReopenUnlimited),
  // In unlimited mode the lower "created within last X days" bound is dropped
  // (BE ignores it), so omit the numeric threshold from the payload entirely.
  tickets_reopen_start_threshold: fields.ticketsReopenUnlimited
    ? undefined
    : fields.ticketsReopenStartThreshold,
});

const TicketingQueueWizard: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { getMutationArray } = useContext(FieldsUtilsContext);
  const automationURLData = useAutomationURLData();
  const client = useApolloClient();

  const { onSubmit, createAutomationLoading, editAutomationLoading } =
    useAutomationSubmit({
      automationId: id,
      automationType: AutomationTypeEnum.TICKETING_QUEUES,
    });

  const [syncQueue] = useMutationWrapper(SYNC_QUEUE, {
    refetchQueries: ["GetRemediationQueue"],
  }) as MutationTuple<
    (
      options: syncQueueMutationVariablesType
    ) => Promise<syncQueueMutationResponseType>,
    any
  >;

  const { data, loading } = useQuery(GET_AUTOMATION, {
    variables: {
      id,
    },
    fetchPolicy: "no-cache",
    skip: !id,
  });

  const onSyncQueue = (automationId: string, automationName: string) => {
    syncQueue({ variables: { remediation_queue_id: automationId } })
      .then(() => {
        showToast({
          message: (
            <div className={"modal-text"}>
              The <span className={"modal-live-text"}>{automationName}</span>
              {` Automation was ${!id ? "created" : "updated"} and synced`}
            </div>
          ),
          "data-testid": "rmq-created",
        });
      })
      .catch(() => {
        showToast({
          message: (
            <div className={"modal-text"}>
              You have successfully ${!id ? "created" : "updated"} the{" "}
              <span className={"modal-live-text"}>{automationName}</span>{" "}
              Automation.
              <br />
              There was an issue syncing with the ticketing system. Please try
              again later.
            </div>
          ),
          "data-testid": "rmq-created",
        });
      });
  };

  const onSuccess = (
    automationId: string,
    automationName: string,
    shouldSyncNow: boolean
  ) => {
    if (shouldSyncNow) {
      onSyncQueue(automationId, automationName);
    }
    client.cache.evict({ fieldName: "endpoint_fields" });
    history.push(
      `/remediation/monitoring/${integrationTypeToPageLinkMap[ticketEndpointTypeEnum.TICKET]}`
    );
  };

  const validateTicketingInputParams = (params: any): boolean => {
    if (!params) {
      return false;
    }

    try {
      const parsedParams = JSON.parse(params);

      return (
        parsedParams.ticket_provider_id &&
        parsedParams.ticket_provider_type &&
        parsedParams.field_mapping_id &&
        parsedParams.queue_size &&
        typeof parsedParams.days_to_reopen_tickets !== "undefined" &&
        typeof parsedParams.template_id !== "undefined"
      );
    } catch (e) {
      return false;
    }
  };

  const automationData = useMemo(() => {
    if (
      !!data?.get_automation &&
      validateAutomationData(data?.get_automation, validateTicketingInputParams)
    ) {
      const automation = data?.get_automation;
      const params: TicketingQueueInputParams = JSON.parse(
        automation?.action?.params
      );

      return { automation, params };
    }
  }, [data]);

  const wizardData:
    | TicketingQueueWizardFormFields
    | AutomationURLDataReturn
    | undefined = useMemo(() => {
    if (!!automationData) {
      const { automation, params } = automationData;

      let data: TicketingQueueWizardFormFields = {
        scope: {
          value: {
            id:
              automation.selection_rule?.scopesid ||
              automation.selection_rule?.scope_group,
          },
        },
        filter: {
          value: {
            id: automation.selection_rule?.filtersid,
          },
        },
        name: automation.name,
        integrationType: params.ticket_provider_type,
        queueSize: params.queue_size,
        ticketProvider: params.ticket_provider_id,
        ...reopenFieldsFromParams(params),
        enableSlaReminder: params.tickets_sla_reminder_enabled,
        schedule: automation.trigger.value,
      };

      if (params.days_to_reopen_tickets > 0) {
        data.daysToReopenTickets = params.days_to_reopen_tickets;
      }

      if (params.tickets_sla_reminder_enabled) {
        data.daysBeforeSlaToRemind =
          params.tickets_sla_reminder_days_before_to_start;
        data.slaReminderDaysInterval =
          params.tickets_sla_reminder_days_interval;
        data.slaReminderMaxTimes = params.tickets_sla_reminder_max_times;
      }

      return data;
    }

    if (!!automationURLData) {
      return automationURLData;
    }
  }, [automationData, automationURLData]);

  const wizardName = useAutomationWizardName(
    "Ticketing Queue",
    automationData?.automation?.name
  );

  const steps = useMemo(() => {
    return [
      {
        name: "Findings",
        id: "findings",
        form: <FindingsStep />,
      },
      {
        name: "Ticketing System",
        id: "ticketing_system",
        form: (
          <TicketingSystemStep
            fieldMappingId={automationData?.params?.field_mapping_id}
          />
        ),
      },
      {
        name: "Ticket Customization",
        id: "ticket_customization",
        form: (
          <CustomizationStep
            ticketingQueueId={id}
            fieldMappingId={automationData?.params?.field_mapping_id}
            templateId={automationData?.params?.template_id}
          />
        ),
      },
      {
        name: "Ticket Monitoring",
        id: "ticket_monitoring",
        form: <TicketMonitoringStep />,
      },
      {
        name: "Ticket Scheduling",
        id: "ticket_scheduling",
        form: <TicketSchedulingStep />,
      },
    ];
  }, [id, automationData]);

  const handleSubmit = useCallback(
    (data: Record<string, any>) => {
      const params: TicketingQueueOutputParams = {
        ticket_provider_id: data.ticketProvider,
        queue_size: Number(data.queueSize),
        ...reopenParamsFromFields({
          ticketsReopenEnabled: data.ticketsReopenEnabled,
          ticketsReopenUnlimited: data.ticketsReopenUnlimited,
          daysToReopenTickets: data.daysToReopenTickets,
          ticketsReopenStartThreshold: data.ticketsReopenStartThreshold,
        }),
        template_id: Number(data.template?.id),
        report_fields: data.columns,
        keys: data.keys,
        tickets_sla_reminder_enabled: data.enableSlaReminder,
        fields: getMutationArray(data.fields)?.reduce(
          (acc: any, obj: any) => ({ ...acc, [obj.key]: obj.value }),
          {}
        ),
      };

      if (data.enableSlaReminder) {
        params.tickets_sla_reminder_days_interval = Number(
          data.slaReminderDaysInterval
        );
        params.tickets_sla_reminder_max_times = Number(
          data.slaReminderMaxTimes
        );
        params.tickets_sla_reminder_days_before_to_start = Number(
          data.daysBeforeSlaToRemind
        );
      }
      const shouldShowSuccessToast = !data.syncNow;

      onSubmit(
        {
          filter: data.filter,
          scope: data.scope,
          name: data.name,
          schedule: data.schedule,
          params: params,
        },
        (automationId, automationName) =>
          onSuccess(automationId, automationName, data.syncNow),
        shouldShowSuccessToast
      );
    },
    [onSubmit, getMutationArray]
  );

  return id && !automationData && !loading ? (
    <PageNotFound
      subtitle={"Oops! Something went wrong..."}
      description={"For help, contact Customer Services"}
      showReachOutButton={false}
    />
  ) : (
    <FormWizard
      type={formVariantsEnum.PAGE}
      originName={id ? "Edit Automation" : "Add Automation"}
      wizardName={wizardName}
      withSummary={true}
      steps={steps}
      onCancel={() => {
        history.goBack();
      }}
      onSubmit={handleSubmit}
      isSubmitting={id ? editAutomationLoading : createAutomationLoading}
      dangerSubmit={false}
      isValidated={!!id}
      submitLabel={`${id ? "Save" : "Create"}`}
      wizardData={wizardData}
    />
  );
};

export default TicketingQueueWizard;
